import { Modal } from '@studiometa/ui';
import { transition, untrapFocus, saveActiveElement } from '@studiometa/js-toolkit/utils';
import { togglePageBackdrop } from '../utils/pageBackdrop.js';
import ThumbnailWipe from '../utils/ThumbnailWipe.js';

/**
 * ArticleModal.
 *
 * Subclass of `@studiometa/ui` Modal that:
 * - scales up from the bottom edge and scales back down to it on close;
 * - scales down + blurs the page behind it while open (see {@link togglePageBackdrop});
 * - animates a hover wipe on its thumbnail (see {@link ThumbnailWipe}).
 *
 * The content inside the modal starts hidden (opacity 0) and fades in all at
 * once after the scale-up animation completes; the site-wide [data-fade] and
 * [data-modal-reveal] slide-up animations are bypassed (opacity only).
 *
 * Overlay click does not close the modal, but the close button and the ESC key do.
 * @augments {Modal}
 */
export default class ArticleModal extends Modal {
  /**
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    ...Modal.config,
    name: 'ArticleModal',
    refs: [...(Modal.config.refs ?? []), 'thumbnail[]'],
    options: {
      ...Modal.config.options,
      styles: {
        type: Object,
        default: () => ({
          modal: {
            closed: {
              opacity: '0',
              pointerEvents: 'none',
              visibility: 'hidden',
            },
            active: {
              transition: 'opacity 0.3s ease',
            },
          },
          container: {
            closed: {
              transform: 'scale(0)',
              transformOrigin: 'center bottom',
            },
            active: {
              transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
            },
            open: {
              transform: 'scale(1)',
              transformOrigin: 'center bottom',
            },
          },
        }),
      },
    },
  };

  /**
   * Lazily-built hover wipe controller for the thumbnail layers.
   * @returns {ThumbnailWipe}
   */
  get thumbnailWipe() {
    if (!this.__thumbnailWipe) {
      this.__thumbnailWipe = new ThumbnailWipe(this.$refs.thumbnail);
    }
    return this.__thumbnailWipe;
  }

  /**
   * The modal content elements that should fade in once the modal is open
   * (`[data-fade]` site-wide fades + `[data-modal-reveal]` blocks).
   * @returns {HTMLElement[]}
   */
  get contentElements() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.$refs.modal.querySelectorAll('[data-fade], [data-modal-reveal]'))
    );
  }

  /**
   * Snap every content element to its hidden state (opacity 0, offset down,
   * no transition) so the modal opens on an invisible body of content ready
   * to be revealed with a bottom-to-top slide.
   * @returns {void}
   */
  hideContent() {
    for (const el of this.contentElements) {
      el.style.transition = 'none';
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      el.classList.remove('inview');
    }
  }

  /**
   * Fade every content element in at once with a soft bottom-to-top slide.
   * Inline styles override the site-wide `[data-fade]` CSS so we control the
   * timing precisely and the IntersectionObserver-driven Fade can't re-hide
   * them while the user scrolls inside the modal.
   * @returns {void}
   */
  revealContent() {
    const easing = 'cubic-bezier(0.215, 0.61, 0.355, 1)';
    for (const el of this.contentElements) {
      // Force a reflow so the switch from `transition: none` (set in
      // `hideContent`) to the animated one is registered by the browser.
      void el.offsetWidth;
      el.style.transition = `opacity 0.8s ${easing}, transform 0.8s ${easing}`;
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
      el.classList.add('inview');
    }
  }

  /**
   * Disable closing on overlay click.
   * @returns {Function} A no-op handler so the overlay click does not close the modal.
   */
  get onOverlayClick() {
    return () => {
      // Overlay click is intentionally a no-op: the modal only closes via the close button.
    };
  }

  /**
   * Open the modal: scale/blur the page, then scale the container up from its
   * bottom edge after a short delay so the backdrop effect is visible first.
   * @returns {Promise<void>}
   */
  async open() {
    if (this.isOpen) {
      return Promise.resolve();
    }

    this.$refs.modal.setAttribute('aria-hidden', 'false');
    if (this.$options.scrollLock) {
      document.documentElement.style.overflow = 'hidden';
    }
    this.isOpen = true;
    this.$emit('open');
    togglePageBackdrop(true);

    const { container, modal } = this.$refs;

    container.scrollTop = 0;
    this.hideContent();

    // Show the wrapper instantly…
    modal.style.opacity = '';
    modal.style.pointerEvents = '';
    modal.style.visibility = '';

    // Trigger the content fade-in shortly before the scale animation ends so the
    // reveal feels in sync with the modal landing. Scale total = 0.4s delay +
    // 0.5s duration = 0.9s, we start the reveal at ~0.7s.
    setTimeout(() => this.revealContent(), 700);

    // …then scale the container up from the bottom edge, after a delay so the
    // page backdrop (scale + blur) is visible before the modal covers it.
    await transition(
      container,
      {
        from: { transform: 'scale(0)', transformOrigin: 'center bottom' },
        active: { transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.4s' },
        to: { transform: 'scale(1)', transformOrigin: 'center bottom' },
      },
      'keep',
    );

    // Remove the (identity) transform so `position: fixed` descendants are
    // positioned against the viewport again instead of the transformed container.
    container.style.transform = '';
    container.style.transformOrigin = '';
    container.style.transition = '';

    const { autofocus } = this.$options;
    if (autofocus && modal.querySelector(autofocus)) {
      saveActiveElement();
      modal.querySelector(autofocus).focus();
    }

    return Promise.resolve();
  }

  /**
   * Close the modal by scaling the container back down to its bottom edge,
   * and reset the page backdrop.
   * @returns {Promise<void>}
   */
  async close() {
    if (!this.isOpen) {
      return Promise.resolve();
    }

    this.$refs.modal.setAttribute('aria-hidden', 'true');
    if (this.$options.scrollLock) {
      document.documentElement.style.overflow = '';
    }
    this.isOpen = false;
    untrapFocus();
    this.$emit('close');
    togglePageBackdrop(false);

    const { container, modal } = this.$refs;

    // Scale the container down to its bottom edge first…
    await transition(
      container,
      {
        from: { transform: 'scale(1)', transformOrigin: 'center bottom' },
        active: { transition: 'transform 0.6s cubic-bezier(0.65, 0, 0.35, 1)' },
        to: { transform: 'scale(0)', transformOrigin: 'center bottom' },
      },
      'keep',
    );

    // …then hide the wrapper once the slide is done (visibility can't animate).
    await transition(
      modal,
      {
        to: { opacity: '0', pointerEvents: 'none', visibility: 'hidden' },
      },
      'keep',
    );

    return Promise.resolve();
  }

  /**
   * Reveal the thumbnail on hover.
   * @returns {Promise<unknown>}
   */
  onOpenMouseenter() {
    return this.thumbnailWipe.reveal();
  }

  /**
   * Hide the thumbnail when the hover ends.
   * @returns {Promise<unknown>}
   */
  onOpenMouseleave() {
    return this.thumbnailWipe.hide();
  }
}
